import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import BrandMark from './BrandMark';
import { site } from '../../content/siteCopy';
import './SiteHeader.css';

const navigation = site.navigation;

function NavigationLinks({ onNavigate, className }) {
  return (
    <nav className={className} aria-label="Primary navigation">
      {navigation.map(item => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `site-header__nav-link${isActive ? ' site-header__nav-link--active' : ''}`
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SiteHeader({ siteName }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const brandRef = useRef(null);
  const menuButtonRef = useRef(null);
  const mobilePanelRef = useRef(null);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleMenuKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableItems = [
        menuButtonRef.current,
        ...(mobilePanelRef.current?.querySelectorAll(
          'a[href], button:not([disabled])',
        ) || []),
      ].filter(Boolean);
      const firstItem = focusableItems[0];
      const lastItem = focusableItems[focusableItems.length - 1];
      const activeItem = document.activeElement;

      if (!focusableItems.includes(activeItem)) {
        event.preventDefault();
        firstItem?.focus();
      } else if (event.shiftKey && activeItem === firstItem) {
        event.preventDefault();
        lastItem?.focus();
      } else if (!event.shiftKey && activeItem === lastItem) {
        event.preventDefault();
        firstItem?.focus();
      }
    };
    const desktopMedia = window.matchMedia?.('(min-width: 48.0625rem)');
    const closeAtDesktopBreakpoint = event => {
      if (event.matches) {
        const activeItem = document.activeElement;
        const shouldRestoreFocus = activeItem === menuButtonRef.current
          || mobilePanelRef.current?.contains(activeItem);

        setIsMenuOpen(false);
        if (shouldRestoreFocus) {
          brandRef.current?.focus();
        }
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleMenuKeyDown);
    if (desktopMedia?.addEventListener) {
      desktopMedia.addEventListener('change', closeAtDesktopBreakpoint);
    } else {
      desktopMedia?.addListener?.(closeAtDesktopBreakpoint);
    }

    if (desktopMedia?.matches) {
      closeAtDesktopBreakpoint(desktopMedia);
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleMenuKeyDown);
      if (desktopMedia?.removeEventListener) {
        desktopMedia.removeEventListener('change', closeAtDesktopBreakpoint);
      } else {
        desktopMedia?.removeListener?.(closeAtDesktopBreakpoint);
      }
    };
  }, [isMenuOpen]);

  return (
    <header className="site-header">
      <a className="site-header__skip-link" href="#main-content">
        Skip to content
      </a>
      <div className="site-header__inner">
        <Link
          ref={brandRef}
          className="site-header__brand"
          to="/"
          aria-label={`${siteName} home`}
        >
          <BrandMark className="site-header__brand-mark" />
          <span className="site-header__brand-name">{siteName}</span>
        </Link>

        <NavigationLinks className="site-header__desktop-nav" />

        <button
          ref={menuButtonRef}
          className="site-header__menu-button"
          type="button"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation"
          aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          onClick={() => setIsMenuOpen(current => !current)}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>

        {isMenuOpen && (
          <div
            ref={mobilePanelRef}
            className="site-header__mobile-panel"
            id="mobile-navigation"
          >
            <NavigationLinks
              className="site-header__mobile-nav"
              onNavigate={() => setIsMenuOpen(false)}
            />
          </div>
        )}
      </div>
    </header>
  );
}

export default SiteHeader;
